/**
 * Cockpit Shell Preview — visual sign-off surface for the locked Cockpit
 * vocabulary. Renders every primitive in `CockpitShell.tsx` against sample
 * data so the design language can be approved before batch-converting the
 * remaining ~40 pages.
 *
 * No business logic. No data fetching. Pure visual demo.
 *
 * Route: /cockpit-shell-preview
 */
import { useState } from "react";
import {
  CockpitPage,
  CockpitMasthead,
  HeroMetric,
  HeroDivider,
  CockpitKpiStrip,
  CockpitTabs,
  CockpitFilterBar,
  CockpitChipGroup,
  CockpitSearch,
  CockpitListShell,
  CockpitColumns,
  CockpitRow,
  CockpitFinancialTable,
  CockpitSection,
  CockpitSplitLayout,
  CockpitKanban,
  CockpitKanbanColumn,
  CockpitKanbanCard,
  CockpitCalendarFrame,
  CockpitEmpty,
  SectionEyebrow,
  StatusDot,
  InlineMeta,
  toneFor,
  type CockpitColumn,
} from "@/components/cockpit/CockpitShell";

/* ── Sample data ────────────────────────────────────────────────── */

const SAMPLE_LEADS = [
  { id: "L-2041", customer: "Patel, R.",     city: "Tampa",       source: "Google",    score: 92, status: "New",     age: "12m" },
  { id: "L-2040", customer: "O'Connor, M.",  city: "Brandon",     source: "Referral",  score: 78, status: "Contacted", age: "1h" },
  { id: "L-2039", customer: "Vasquez, D.",   city: "Riverview",   source: "Facebook",  score: 64, status: "Booked",  age: "3h" },
  { id: "L-2038", customer: "Nguyen, H.",    city: "Lakeland",    source: "Door",      score: 41, status: "Stalled", age: "2d" },
];

interface FinRow { rep: string; deals: number; revenue: number; commission: number; rate: number; }
const SAMPLE_FIN: FinRow[] = [
  { rep: "Avery Quinn",     deals: 14, revenue: 198_400, commission: 19_840, rate: 0.10 },
  { rep: "Marcus Bell",     deals: 11, revenue: 162_900, commission: 14_661, rate: 0.09 },
  { rep: "Sasha Lin",       deals:  9, revenue: 121_500, commission: 10_935, rate: 0.09 },
  { rep: "Demetri Fields",  deals:  7, revenue:  88_300, commission:  6_181, rate: 0.07 },
];

const fmt$ = (n: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
const fmtPct = (n: number) => `${(n * 100).toFixed(1)}%`;

const FIN_COLS: CockpitColumn<FinRow>[] = [
  { key: "rep",        label: "Rep",         render: r => r.rep },
  { key: "deals",      label: "Deals",       render: r => r.deals,                       numeric: true },
  { key: "revenue",    label: "Revenue",     render: r => fmt$(r.revenue),               numeric: true },
  { key: "commission", label: "Commission",  render: r => fmt$(r.commission),            numeric: true },
  { key: "rate",       label: "Rate",        render: r => fmtPct(r.rate),                numeric: true },
];

/* ── Page ───────────────────────────────────────────────────────── */

export default function CockpitShellPreview() {
  const [tab, setTab] = useState("all");
  const [chip, setChip] = useState("active");
  const [search, setSearch] = useState("");
  const [selectedLead, setSelectedLead] = useState(SAMPLE_LEADS[0]);

  return (
    <CockpitPage>
      {/* ── Masthead + hero metrics ─────────────────────────────── */}
      <CockpitMasthead
        eyebrow="Design system"
        title="Cockpit shell vocabulary"
        right={
          <>
            <HeroMetric label="Primitives" value={14} />
            <HeroDivider />
            <HeroMetric label="Pages to convert" value={40} hero />
            <HeroDivider />
            <HeroMetric label="Status" value="Ready" tone="good" />
          </>
        }
      />

      <p className="text-sm text-muted-foreground max-w-2xl -mt-2">
        Every primitive in the locked Cockpit vocabulary. Sign off here before the
        batch conversion begins. Sample data only — no business logic.
      </p>

      {/* ── KPI strip ───────────────────────────────────────────── */}
      <Demo title="CockpitKpiStrip" subtitle="Hairline metric row for dashboards without a single dominant hero.">
        <CockpitKpiStrip
          items={[
            { label: "Active jobs",   value: 142,            tone: "default" },
            { label: "At risk",       value: 8,              tone: "warn", hint: "↑ 2 vs yesterday" },
            { label: "Blocked",       value: 3,              tone: "bad" },
            { label: "Ready today",   value: 17,             tone: "good" },
            { label: "Revenue MTD",   value: "$1.24M",       tone: "default", hint: "78% of plan" },
          ]}
        />
      </Demo>

      {/* ── Tabs + Filter bar ──────────────────────────────────── */}
      <Demo title="CockpitTabs" subtitle="Underlined hairline tabs with optional counts.">
        <CockpitTabs
          tabs={[
            { value: "all",       label: "All",       count: 142 },
            { value: "active",    label: "Active",    count: 87 },
            { value: "atrisk",    label: "At risk",   count: 8 },
            { value: "blocked",   label: "Blocked",   count: 3 },
            { value: "complete",  label: "Complete",  count: 44 },
          ]}
          value={tab}
          onChange={setTab}
        />
      </Demo>

      <Demo title="CockpitFilterBar + CockpitSearch + CockpitChipGroup" subtitle="Single-row filter rail. Search left, chips middle, controls right.">
        <CockpitFilterBar
          search={
            <CockpitSearch
              value={search}
              onChange={setSearch}
              placeholder="Search leads, jobs, customers"
            />
          }
          chips={
            <CockpitChipGroup
              options={[
                { value: "all",      label: "All",       count: 142 },
                { value: "active",   label: "Active",    count: 87 },
                { value: "atrisk",   label: "At risk",   count: 8 },
                { value: "stalled",  label: "Stalled",   count: 12 },
              ]}
              value={chip}
              onChange={setChip}
            />
          }
          right={
            <button className="h-9 px-3 text-sm border border-border/60 rounded-sm hover:border-foreground/40 transition-colors">
              Export
            </button>
          }
        />
      </Demo>

      {/* ── List shell + rows ──────────────────────────────────── */}
      <Demo title="CockpitListShell + CockpitColumns + CockpitRow" subtitle="Hairline list. Hover affordance, optional ChevronRight, link or button.">
        <div>
          <CockpitColumns template="80px 1fr 140px 120px 80px 90px">
            <span>ID</span>
            <span>Customer</span>
            <span>City</span>
            <span>Source</span>
            <span className="text-right">Score</span>
            <span>Status</span>
          </CockpitColumns>
          <CockpitListShell>
            {SAMPLE_LEADS.map(l => (
              <CockpitRow
                key={l.id}
                template="80px 1fr 140px 120px 80px 90px"
                onClick={() => setSelectedLead(l)}
              >
                <span className="text-sm tabular-nums text-muted-foreground">{l.id}</span>
                <span className="text-sm font-medium text-foreground truncate">{l.customer}</span>
                <span className="text-sm text-muted-foreground">{l.city}</span>
                <span className="text-sm text-muted-foreground">{l.source}</span>
                <span className={`text-sm tabular-nums text-right font-medium ${
                  l.score >= 80 ? "text-success" : l.score >= 60 ? "text-warning" : "text-destructive"
                }`}>{l.score}</span>
                <span className="flex items-center gap-1.5 text-sm text-foreground/80">
                  <StatusDot tone={l.status === "Booked" ? "good" : l.status === "Stalled" ? "bad" : "default"} />
                  {l.status}
                </span>
              </CockpitRow>
            ))}
          </CockpitListShell>
        </div>
      </Demo>

      {/* ── Financial table ────────────────────────────────────── */}
      <Demo title="CockpitFinancialTable" subtitle="Dense numeric table with tabular-nums, right-aligned, optional footer.">
        <CockpitFinancialTable
          columns={FIN_COLS}
          rows={SAMPLE_FIN}
          getKey={r => r.rep}
          footer={
            <>
              <td className="py-3 px-4 text-left">Total</td>
              <td className="py-3 px-4 text-right tabular-nums">
                {SAMPLE_FIN.reduce((a, r) => a + r.deals, 0)}
              </td>
              <td className="py-3 px-4 text-right tabular-nums">
                {fmt$(SAMPLE_FIN.reduce((a, r) => a + r.revenue, 0))}
              </td>
              <td className="py-3 px-4 text-right tabular-nums">
                {fmt$(SAMPLE_FIN.reduce((a, r) => a + r.commission, 0))}
              </td>
              <td className="py-3 px-4 text-right tabular-nums text-muted-foreground">—</td>
            </>
          }
        />
      </Demo>

      {/* ── Split layout ───────────────────────────────────────── */}
      <Demo title="CockpitSplitLayout" subtitle="List on the left, detail on the right. Both panes scroll independently. For queues like Lead Integrity, Service Tickets, Pipeline detail.">
        <CockpitSplitLayout
          list={
            <div className="divide-y divide-border/60">
              {SAMPLE_LEADS.map(l => {
                const active = selectedLead.id === l.id;
                return (
                  <button
                    key={l.id}
                    onClick={() => setSelectedLead(l)}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      active ? "bg-muted/40" : "hover:bg-muted/20"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground">{l.customer}</span>
                      <span className="text-xs tabular-nums text-muted-foreground">{l.id}</span>
                    </div>
                    <InlineMeta items={[l.city, l.source, `${l.age} ago`]} className="mt-1" />
                  </button>
                );
              })}
            </div>
          }
          detail={
            <div className="border border-border/60 rounded-sm bg-background p-6 space-y-4">
              <SectionEyebrow>Lead detail</SectionEyebrow>
              <div className="space-y-1">
                <h3 className="text-xl font-semibold tracking-tight">{selectedLead.customer}</h3>
                <InlineMeta items={[selectedLead.id, selectedLead.city, selectedLead.source]} />
              </div>
              <div className="grid grid-cols-3 gap-6 pt-4 border-t border-border/60">
                <Stat label="Fit score" value={selectedLead.score} tone={toneFor(selectedLead.score, 80, 60)} />
                <Stat label="Status" value={selectedLead.status} />
                <Stat label="Age" value={selectedLead.age} />
              </div>
            </div>
          }
        />
      </Demo>

      {/* ── Kanban ─────────────────────────────────────────────── */}
      <Demo title="CockpitKanban + Column + Card" subtitle="Horizontal-scrolling board. Hairline cards, no shadows. For Pipeline, Build Review, Permitting.">
        <CockpitKanban>
          <CockpitKanbanColumn title="Intake" count={4}>
            <KanbanSampleCard name="Patel, R." meta="Tampa · Walk-in shower" />
            <KanbanSampleCard name="Vasquez, D." meta="Riverview · Tub conversion" />
            <KanbanSampleCard name="Nguyen, H." meta="Lakeland · Full bath" />
            <KanbanSampleCard name="Park, S." meta="Brandon · Walk-in shower" />
          </CockpitKanbanColumn>
          <CockpitKanbanColumn title="Build Review" count={2} tone="warn">
            <KanbanSampleCard name="O'Connor, M." meta="Brandon · Full bath" />
            <KanbanSampleCard name="Reyes, A." meta="Tampa · Tub conversion" />
          </CockpitKanbanColumn>
          <CockpitKanbanColumn title="Ordering" count={3}>
            <KanbanSampleCard name="Khan, J." meta="Lakeland · Walk-in shower" />
            <KanbanSampleCard name="Singh, P." meta="Tampa · Full bath" />
            <KanbanSampleCard name="Allen, B." meta="Brandon · Tub conversion" />
          </CockpitKanbanColumn>
          <CockpitKanbanColumn title="Ready" count={5} tone="good">
            <KanbanSampleCard name="Morgan, T." meta="Tampa · Walk-in shower" />
            <KanbanSampleCard name="Hayes, V." meta="Riverview · Full bath" />
            <KanbanSampleCard name="Brooks, E." meta="Lakeland · Walk-in shower" />
            <KanbanSampleCard name="Cole, F." meta="Brandon · Tub conversion" />
            <KanbanSampleCard name="Diaz, R." meta="Tampa · Full bath" />
          </CockpitKanbanColumn>
          <CockpitKanbanColumn title="Blocked" count={1} tone="bad">
            <KanbanSampleCard name="Lopez, K." meta="Lakeland · Permit hold" />
          </CockpitKanbanColumn>
        </CockpitKanban>
      </Demo>

      {/* ── Calendar frame ─────────────────────────────────────── */}
      <Demo title="CockpitCalendarFrame" subtitle="Wraps existing calendar components in Cockpit chrome without restyling internals.">
        <CockpitCalendarFrame>
          <div className="p-8 text-center text-muted-foreground text-sm bg-muted/10">
            <p className="mb-2 font-medium text-foreground">[ Calendar component renders here ]</p>
            <p>Used to embed the existing role-aware scheduling calendar without touching its internals.</p>
          </div>
        </CockpitCalendarFrame>
      </Demo>

      {/* ── Section + atoms ────────────────────────────────────── */}
      <CockpitSection
        eyebrow="Atoms"
        title="Helpers & indicators"
        actions={
          <button className="h-8 px-3 text-xs border border-border/60 rounded-sm hover:border-foreground/40 transition-colors">
            Action
          </button>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border border-border/60 rounded-sm p-5 space-y-3">
            <SectionEyebrow>StatusDot</SectionEyebrow>
            <div className="flex flex-col gap-2 text-sm">
              <span className="flex items-center gap-2"><StatusDot tone="good" /> Healthy</span>
              <span className="flex items-center gap-2"><StatusDot tone="warn" /> At risk</span>
              <span className="flex items-center gap-2"><StatusDot tone="bad" /> Blocked</span>
              <span className="flex items-center gap-2"><StatusDot tone="muted" /> Idle</span>
              <span className="flex items-center gap-2"><StatusDot /> Default</span>
            </div>
          </div>
          <div className="border border-border/60 rounded-sm p-5 space-y-3">
            <SectionEyebrow>InlineMeta</SectionEyebrow>
            <InlineMeta items={["Tampa", "3 jobs", "Updated 2h ago"]} />
            <InlineMeta items={["L-2041", "Walk-in shower", "$14,200", false, "Patel"]} />
            <InlineMeta items={["Avery Quinn", "10% rate"]} />
          </div>
        </div>
      </CockpitSection>

      {/* ── Empty ──────────────────────────────────────────────── */}
      <Demo title="CockpitEmpty" subtitle="Centered empty state for lists & tables.">
        <div className="border-y border-border/60">
          <CockpitEmpty message="No records match the current filters." />
        </div>
      </Demo>

      <div className="pt-8 border-t border-border/60 text-center">
        <p className="text-xs text-muted-foreground">
          Vocabulary frozen. Sign off in chat to begin the 3-pass page conversion.
        </p>
      </div>
    </CockpitPage>
  );
}

/* ── Local helpers (preview-only) ───────────────────────────────── */

function Demo({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3 pt-2">
      <div className="space-y-1">
        <h2 className="text-sm font-medium tracking-tight text-foreground font-mono">{title}</h2>
        {subtitle && <p className="text-xs text-muted-foreground max-w-2xl">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

function Stat({ label, value, tone = "default" }: { label: string; value: string | number; tone?: "default" | "good" | "warn" | "bad" }) {
  const toneCls =
    tone === "good" ? "text-success" :
    tone === "warn" ? "text-warning" :
    tone === "bad" ? "text-destructive" : "text-foreground";
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium mb-1">{label}</p>
      <p className={`text-lg font-semibold tabular-nums tracking-tight ${toneCls}`}>{value}</p>
    </div>
  );
}

function KanbanSampleCard({ name, meta }: { name: string; meta: string }) {
  return (
    <CockpitKanbanCard>
      <p className="text-sm font-medium text-foreground">{name}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{meta}</p>
    </CockpitKanbanCard>
  );
}
